import { ChevronRight, Box } from 'lucide-react';
import { blocks as mockBlocks } from '@/data/blockchainData';
import { TronSocketData } from '@/hooks/useTronSocket';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '@/lib/timeUtils';

interface BlocksListProps {
  socketData?: TronSocketData;
}

const BlocksList = ({ socketData }: BlocksListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const blocks = useMemo(() => {
    if (socketData?.latestBlocks && socketData.latestBlocks.length > 0) {
      return socketData.latestBlocks.map(block => ({
        number: block.number,
        hash: block.hash,
        timestamp: new Date(block.timestamp),
        validatorName: block.producerName || block.producer.slice(0, 8) + '...',
        validatorAddress: block.producer,
        transactionCount: block.txCount,
        reward: block.reward,
        burned: Math.floor(block.fee),
      }));
    }
    return mockBlocks.map(b => ({ ...b, validatorAddress: '' }));
  }, [socketData?.latestBlocks]);

  const handleBlockClick = (blockNumber: number) => {
    navigate(`/search?q=${blockNumber}&type=block`);
  };

  const handleValidatorClick = (address: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (address) {
      navigate(`/search?q=${address}&type=account`);
    }
  };

  return (
    <div className="glass-card overflow-visible">
      <div className="flex items-center p-3 sm:p-5 border-b border-border/50">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg sm:rounded-xl">
            <Box className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-foreground">{t('blocks.title')}</h2>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <div className="flex gap-3 sm:gap-4 p-3 sm:p-5 w-max">
          {blocks.slice(0, 4).map((block, index) => (
            <div
              key={`${block.number}-${index}`}
              className="block-card min-w-[240px] sm:min-w-[300px] flex-shrink-0 p-3 sm:p-4"
              onClick={() => handleBlockClick(block.number)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div>
                  <p className="text-primary font-bold text-base sm:text-lg hover:underline">
                    #{block.number.toLocaleString()}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                    {formatTimeAgo(block.timestamp, t)}
                  </p>
                </div>
                <div className="text-right">
                  <p 
                    className="text-xs sm:text-sm text-primary font-medium flex items-center gap-1 hover:underline cursor-pointer"
                    onClick={(e) => handleValidatorClick(block.validatorAddress, e)}
                  >
                    <span className="truncate max-w-[80px] sm:max-w-none">{block.validatorName}</span>
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                    {block.transactionCount} {t('blocks.txns')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 bg-success/10 rounded-md sm:rounded-lg text-success">
                  <span className="font-medium">{t('blocks.reward')}: {block.reward} TRX</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlocksList;